// /home/heliowallet/frontend/components/wallet/TransactionHistory.tsx
'use client';

import { useState, useEffect, useCallback } from 'react';
import {
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowTopRightOnSquareIcon,
  ArrowDownTrayIcon,
  FunnelIcon,
  ArrowPathIcon,
  DocumentDuplicateIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { usePublicClient } from 'wagmi';
import { transactionService } from '@/services/transactionService';

interface Transaction {
  hash: string;
  from: string;
  to: string;
  value: string;
  tokenAddress?: string;
  tokenSymbol?: string;
  type?: 'send' | 'receive' | 'token' | 'contract';
  status: 'pending' | 'confirmed' | 'failed';
  timestamp: number;
  chainId: number;
}

interface TransactionHistoryProps {
  transactions: Transaction[];
  chainInfo: any;
  chainId: number;
  address: string | undefined;
  onUpdateTransactionStatus?: (txHash: string, status: 'confirmed' | 'failed') => void;
  onRemoveTransaction?: (txHash: string) => void;
  onClearTransactions?: () => void;
  onAddTransactions?: (transactions: Transaction[]) => void;
}

// Thời gian timeout cho pending transaction (5 phút)
const PENDING_TIMEOUT = 5 * 60 * 1000;
// Interval kiểm tra status (30 giây)
const STATUS_CHECK_INTERVAL = 30 * 1000;

export default function TransactionHistory({
  transactions,
  chainInfo,
  chainId,
  address,
  onUpdateTransactionStatus,
  onRemoveTransaction,
  onClearTransactions,
  onAddTransactions
}: TransactionHistoryProps) {
  const [filter, setFilter] = useState<'all' | 'sent' | 'received' | 'pending'>('all');
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [copiedTx, setCopiedTx] = useState<string | null>(null);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [isFetchingHistory, setIsFetchingHistory] = useState(false);
  const publicClient = usePublicClient();

  // Auto-remove old pending transactions
  useEffect(() => {
    const now = Date.now();
    transactions.forEach(tx => {
      if (tx.status === 'pending' && now - tx.timestamp > PENDING_TIMEOUT) {
        // Mark as failed if pending too long
        onUpdateTransactionStatus?.(tx.hash, 'failed');
      }
    });
  }, [transactions, onUpdateTransactionStatus]);

  // Check pending transaction status periodically
  useEffect(() => {
    if (!publicClient || !onUpdateTransactionStatus) return;

    const checkPendingTransactions = async () => {
      const pendingTxs = transactions.filter(tx => tx.status === 'pending');
      
      for (const tx of pendingTxs) {
        try {
          const receipt = await publicClient.getTransactionReceipt({ hash: tx.hash as `0x${string}` });
          if (receipt) {
            onUpdateTransactionStatus(tx.hash, receipt.status === 'success' ? 'confirmed' : 'failed');
          }
        } catch (error) {
          // Transaction might not exist yet or other error
          console.error('Error checking transaction:', error);
        }
      }
    };

    // Check immediately
    checkPendingTransactions();

    // Set up interval
    const interval = setInterval(checkPendingTransactions, STATUS_CHECK_INTERVAL);

    return () => clearInterval(interval);
  }, [transactions, publicClient, onUpdateTransactionStatus]);

  const formatTxHash = (hash: string) => {
    return `${hash.slice(0, 6)}...${hash.slice(-4)}`;
  };

  const formatAddress = (addr: string) => {
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  };

  const formatTimestamp = (timestamp: number) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
    
    return date.toLocaleDateString();
  };

  const getExplorerUrl = (hash: string) => {
    if (chainInfo?.explorers?.[0]) {
      return `${chainInfo.explorers[0].url}/tx/${hash}`;
    }
    return `https://blockscan.com/tx/${hash}`;
  };

  const getExplorerName = () => {
    return chainInfo?.explorers?.[0]?.name || 'Explorer';
  };

  const copyTxHash = (hash: string) => {
    navigator.clipboard.writeText(hash);
    setCopiedTx(hash);
    toast.success('Transaction hash copied');
    setTimeout(() => setCopiedTx(null), 2000);
  };

  const openInExplorer = () => {
    if (chainInfo?.explorers?.[0]?.url) {
      window.open(chainInfo.explorers[0].url, '_blank');
    } else {
      toast.error('No explorer available for this chain');
    }
  };

  const getTransactionIcon = (tx: Transaction) => {
    const isSent = tx.from.toLowerCase() === address?.toLowerCase();
    
    if (isSent) {
      return <ArrowTopRightOnSquareIcon className="h-5 w-5 text-red-600 dark:text-red-400" />;
    } else {
      return <ArrowDownTrayIcon className="h-5 w-5 text-green-600 dark:text-green-400" />;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <ClockIcon className="h-4 w-4 text-yellow-500 animate-pulse" />;
      case 'confirmed':
        return <CheckCircleIcon className="h-4 w-4 text-green-500" />;
      case 'failed':
        return <XCircleIcon className="h-4 w-4 text-red-500" />;
      default:
        return null;
    }
  };

  const filteredTransactions = transactions
    .filter(tx => {
      if (filter === 'all') return true;
      if (filter === 'sent') return tx.from.toLowerCase() === address?.toLowerCase();
      if (filter === 'received') return tx.to.toLowerCase() === address?.toLowerCase();
      if (filter === 'pending') return tx.status === 'pending';
      return true;
    })
    .sort((a, b) => b.timestamp - a.timestamp); // Sắp xếp mới nhất trên đầu

  const filterOptions = [
    { value: 'all', label: 'All Transactions' },
    { value: 'sent', label: 'Sent' },
    { value: 'received', label: 'Received' },
    { value: 'pending', label: 'Pending' }
  ];

  const handleRemoveTransaction = (txHash: string) => {
    onRemoveTransaction?.(txHash);
    toast.success('Transaction removed');
  };

  const handleClearAll = () => {
    onClearTransactions?.();
    setShowClearConfirm(false);
    toast.success('All transactions cleared');
  };

  // Fetch transaction history từ explorer
  const fetchTransactionHistory = async () => {
    if (!address || !chainInfo?.explorers?.[0]) {
      toast.error('No explorer available for this chain');
      return;
    }

    setIsFetchingHistory(true);
    try {
      const explorerUrl = chainInfo.explorers[0].url;
      
      // Fetch both normal transactions and token transfers (tối đa 10 mỗi loại)
      const [normalTxs, tokenTxs] = await Promise.all([
        transactionService.fetchTransactionHistory(address, chainId, explorerUrl, 10),
        transactionService.fetchTokenTransfers(address, chainId, explorerUrl, 10)
      ]);

      // Merge with existing transactions
      const mergedTxs = transactionService.mergeTransactions(transactions as any, normalTxs, tokenTxs);
      
      // Filter out only new transactions
      const newTransactions = mergedTxs.filter(tx => 
        !transactions.some(existing => existing.hash === tx.hash)
      );

      if (newTransactions.length > 0) {
        // Add new transactions to the store
        onAddTransactions?.(newTransactions);
        toast.success(`Found ${newTransactions.length} new transaction(s)`);
      } else {
        toast('No new transactions found', { icon: 'ℹ️' });
      }
    } catch (error) {
      console.error('Failed to fetch transaction history:', error);
      toast.error('Failed to fetch transaction history');
    } finally {
      setIsFetchingHistory(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
      {/* Header */}
      <div className="px-4 py-4 border-b border-gray-100 dark:border-gray-700">
        <div className="flex items-center justify-between mb-3">
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Transaction History
            </h3>
            {chainInfo && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 truncate">
                {chainInfo.name} • {getExplorerName()}
              </p>
            )}
          </div>
          
          {/* Action buttons */}
          <div className="flex items-center gap-1">
            {/* Explorer link */}
            {chainInfo?.explorers?.[0]?.url && (
              <button
                onClick={openInExplorer}
                className="flex items-center gap-1 px-2 py-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                title="Open blockchain explorer"
              >
                <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                <span className="hidden sm:inline text-xs">Explorer</span>
              </button>
            )}
            
            {/* Fetch history button */}
            <button
              onClick={fetchTransactionHistory}
              disabled={isFetchingHistory}
              className="flex items-center gap-1 px-2 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
              title="Fetch transaction history"
            >
              <ArrowPathIcon className={`h-4 w-4 text-gray-600 dark:text-gray-400 ${isFetchingHistory ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline text-xs text-gray-600 dark:text-gray-400">
                {isFetchingHistory ? 'Fetching...' : 'Fetch'}
              </span>
            </button>
            
            {/* Clear all button */}
            {transactions.length > 0 && (
              <button
                onClick={() => setShowClearConfirm(true)}
                className="flex items-center gap-1 px-2 py-2 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                title="Clear all transactions"
              >
                <TrashIcon className="h-4 w-4 text-red-600 dark:text-red-400" />
                <span className="hidden sm:inline text-xs text-red-600 dark:text-red-400">Clear</span>
              </button>
            )}
          </div>
        </div>
        
        {/* Filter */}
        <div className="relative">
          <button
            onClick={() => setShowFilterMenu(!showFilterMenu)}
            className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg transition-colors"
          >
            <div className="flex items-center gap-2">
              <FunnelIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                {filterOptions.find(opt => opt.value === filter)?.label}
              </span>
            </div>
            <ChevronDownIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
          </button>
          
          {/* Filter dropdown */}
          {showFilterMenu && (
            <div className="absolute left-0 right-0 top-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-10">
              <div className="p-1">
                {filterOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => {
                      setFilter(option.value as any);
                      setShowFilterMenu(false);
                    }}
                    className={`w-full px-3 py-2 text-left rounded text-sm hover:bg-gray-100 dark:hover:bg-gray-700 ${
                      filter === option.value ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400' : 'text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Clear confirmation modal */}
      {showClearConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-sm mx-4">
            <div className="flex items-center gap-3 mb-4">
              <ExclamationTriangleIcon className="h-6 w-6 text-yellow-500" />
              <h3 className="text-lg font-semibold">Clear all transactions?</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              This will remove all transaction history for this wallet on {chainInfo?.name || 'this chain'}.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowClearConfirm(false)}
                className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={handleClearAll}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Transaction List */}
      <div className="max-h-80 overflow-y-auto">
        {filteredTransactions.length > 0 ? (
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {filteredTransactions.map((tx) => {
              const isSent = tx.from.toLowerCase() === address?.toLowerCase();
              
              return (
                <div key={`${address}-${chainId}-${tx.hash}-${tx.timestamp}`} className="p-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors group">
                  <div className="flex items-center gap-3">
                    {/* Icon */}
                    <div className={`p-2 rounded-full flex-shrink-0 ${
                      isSent ? 'bg-red-100 dark:bg-red-900/20' : 'bg-green-100 dark:bg-green-900/20'
                    }`}>
                      {getTransactionIcon(tx)}
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <span className="font-medium text-gray-900 dark:text-white text-sm truncate">
                            {isSent ? 'Sent' : 'Received'} {tx.tokenSymbol || chainInfo?.nativeCurrency?.symbol}
                          </span>
                          {getStatusIcon(tx.status)}
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <span className={`font-medium text-sm ${
                            isSent ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'
                          }`}>
                            {isSent ? '-' : '+'}{tx.value}
                          </span>
                          {/* Delete button */}
                          <button
                            onClick={() => handleRemoveTransaction(tx.hash)}
                            className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-red-100 dark:hover:bg-red-900/20 rounded ml-1"
                            title="Remove transaction"
                          >
                            <TrashIcon className="h-3 w-3 text-red-500 dark:text-red-400" />
                          </button>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between mt-1">
                        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 min-w-0 flex-1">
                          <span className="truncate">
                            {isSent ? 'To' : 'From'}: {formatAddress(isSent ? tx.to : tx.from)}
                          </span>
                        </div>
                        <span className="text-xs text-gray-400 flex-shrink-0">
                          {formatTimestamp(tx.timestamp)}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-2 mt-2">
                        <button
                          onClick={() => copyTxHash(tx.hash)}
                          className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                        >
                          {copiedTx === tx.hash ? (
                            <>
                              <CheckCircleIcon className="h-3 w-3 text-green-500" />
                              <span>Copied</span>
                            </>
                          ) : (
                            <>
                              <DocumentDuplicateIcon className="h-3 w-3" />
                              <span className="hidden sm:inline">{formatTxHash(tx.hash)}</span>
                              <span className="sm:hidden">Copy</span>
                            </>
                          )}
                        </button>
                        
                        {tx.status === 'confirmed' && (
                          <a
                            href={getExplorerUrl(tx.hash)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
                          >
                            <span>View</span>
                            <ArrowTopRightOnSquareIcon className="h-3 w-3" />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="p-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full mb-4">
              <ArrowPathIcon className="h-8 w-8 text-gray-400" />
            </div>
            {transactions.length === 0 ? (
              <>
                <p className="text-gray-500 dark:text-gray-400 font-medium">No transactions yet</p>
                <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                  Your transactions will appear here
                </p>
              </>
            ) : (
              <>
                <p className="text-gray-500 dark:text-gray-400 font-medium">No {filter} transactions</p>
                <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                  Try changing the filter or add some transactions
                </p>
              </>
            )}
          </div>
        )}
      </div>

      {/* Pending transactions notice */}
      {transactions.some(tx => tx.status === 'pending') && (
        <div className="px-6 py-3 bg-yellow-50 dark:bg-yellow-900/20 border-t border-gray-100 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <ClockIcon className="h-4 w-4 text-yellow-600 dark:text-yellow-400 animate-pulse" />
            <p className="text-sm text-yellow-700 dark:text-yellow-300">
              {transactions.filter(tx => tx.status === 'pending').length} pending transaction(s)
            </p>
          </div>
        </div>
      )}
    </div>
  );
}